import React, { useState } from "react";
import { useSignedUrl } from "../../hooks/use_signed_url";

interface DroneGalleryProps {
  imageKeys: string[];
}

const DroneGallery: React.FC<DroneGalleryProps> = ({ imageKeys }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const isDev = process.env.NODE_ENV === 'development';

  // Get signed URLs for all images - call hooks at top level
  const signedUrls = [];
  for (let i = 0; i < Math.min(imageKeys.length, 10); i++) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const signedUrl = useSignedUrl(imageKeys[i]);
    if (signedUrl) {
      signedUrls.push(signedUrl);
    }
  }
  
  if (!imageKeys.length || !signedUrls.length) {
    return (
      <div className="mb-6 bg-gray-100 p-4 rounded-lg text-center">
        <p>No images available</p>
        {isDev && <p className="text-xs mt-2 text-gray-500">Keys: {imageKeys.join(', ')}</p>}
      </div>
    );
  }

  const selectedImageUrl = signedUrls[selectedImageIndex];

  return (
    <div className="mb-6">
      {/* Main Image Display */}
      <div className="mb-4 flex justify-center">
        <img
          src={selectedImageUrl}
          alt={`Drone image ${selectedImageIndex + 1}`}
          className="max-w-full max-h-[600px] w-auto h-auto object-contain rounded-lg"
          loading="lazy"
          onError={(e) => {
            console.error("Image failed to load:", selectedImageUrl);
            e.currentTarget.onerror = null;
            e.currentTarget.style.display = "none";
            // Add fallback element
            const fallback = document.createElement("div");
            fallback.className = "bg-gray-100 p-4 rounded-lg text-center h-64 flex items-center justify-center";
            fallback.innerHTML = `<p>Failed to load image</p>${isDev ? `<p class="text-xs mt-2 text-gray-500">URL: ${selectedImageUrl?.substring(0, 50)}...</p>` : ''}`;
            e.currentTarget.parentElement?.appendChild(fallback);
          }}
        />
      </div>

      {/* Thumbnail Gallery */}
      {signedUrls.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {signedUrls.map((url, index) => (
            <button
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                selectedImageIndex === index
                  ? 'border-primary shadow-lg'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={url}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ maxWidth: '80px', maxHeight: '80px' }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Image Counter */}
      {signedUrls.length > 1 && (
        <div className="text-center text-sm text-gray-500 mt-2">
          {selectedImageIndex + 1} of {signedUrls.length}
        </div>
      )}
    </div>
  );
};

export default DroneGallery;
